import { nextDriver, resolveDriver } from '@racedash/shared'
import { describeDriver } from '../screenContext'
import { Datum, NoValue, Tile } from '../Tile'
import { useDriverStints } from '../useDriverStints'
import type { WidgetProps } from './props'

/**
 * Who is in the car, and who is due next.
 *
 * The current driver is resolved exactly as the stint chart resolves it — the
 * team's declaration where there is one, the timing system's guess otherwise —
 * so the two screens cannot name different people. Either way the name shown
 * is the roster's where the vendor's abbreviation can be pinned to one, so the
 * top line and "Next up" spell people the same way.
 *
 * "Next up" comes from the plan's rotation, and where the plan and the car
 * disagree it says so. That disagreement is worth seeing: a rotation departed
 * from silently is how a driver ends up over their stint count.
 *
 * Deliberately no stint duration here. The figure is real and other tiles use
 * it, but on this tile it competed with the name for the space and answered a
 * question nobody was asking of it.
 */
export default function Driver({ car, data, now, context }: WidgetProps<'driver'>) {
  const { stints, at } = useDriverStints(car, data, context, now)
  const current = stints.at(-1) ?? null

  // Declared first; the vendor's field where nobody has said. The same order
  // the stint chart and the lap table use.
  const vendor = data.laps.find((l) => l.car === car)?.driver
  const resolved = car
    ? resolveDriver(context.declarations, car, current?.from ?? at, vendor ?? undefined)
    : { source: 'none' as const }
  const shown = describeDriver(context, car, resolved)

  // Whoever we believe is in the car, by roster id, from either source. The
  // rotation cannot say who is next without knowing who is now.
  const currentId = current?.driverId ?? shown.driverId
  const rotation = context.plan?.drivers ?? []
  const next = rotation.length > 0 ? describeDriver(context, car, idOf(nextDriver(rotation, currentId))).name : null

  // The plan expected somebody else in the car. Not an error — plans get
  // departed from for good reasons — but it is the thing nobody notices.
  const offPlan = rotation.length > 0 && currentId != null && !rotation.includes(currentId)

  return (
    <Tile label="Driver">
      {/* A column of its own, because the tile's body is a row. Without it the
          name and the line below sit side by side, and the name — which is set
          to ellipsis when it runs out of room — gets truncated at whatever
          "Next up: Someone" leaves over. The longer the next driver's name, the
          shorter the current one appeared. */}
      <div className="driver">
        {shown.name ? <Datum value={shown.name} as="text" /> : <NoValue as="text" />}
        <div className="driver-next">
          {next && <span>Next up: {next}</span>}
          {offPlan && <span className="driver-drift">not in the rotation</span>}
        </div>
      </div>
    </Tile>
  )
}

function idOf(id: string | null): { source: 'team'; driverId: string } | { source: 'none' } {
  return id ? { source: 'team', driverId: id } : { source: 'none' }
}
